import React, { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { longevityData } from './longevityData';
import './LongevityCalculator.css';

const GRAPH_MAX_AGE = longevityData.graphMaxAge ?? 120;
const MAX_START_AGE = 100;
const FACTOR_COUNT = 6;

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function formatNumber(value) {
    if (value === null || !Number.isFinite(value)) {
        return '—';
    }

    return value.toFixed(1);
}

function gompertzSurvival(age, a, b) {
    if (
        a === null ||
        b === null ||
        !Number.isFinite(a) ||
        !Number.isFinite(b) ||
        b === 0
    ) {
        return null;
    }

    const bx = clamp(b * age, -700, 700);

    let cumulativeHazard =
        (a / b) * (Math.exp(bx) - 1);

    cumulativeHazard = clamp(
        cumulativeHazard,
        0,
        700
    );

    return Math.exp(-cumulativeHazard);
}

function createForecastPoints(state) {
    const a = state?.[0];
    const b = state?.[1];

    if (
        a === null ||
        b === null ||
        !Number.isFinite(a) ||
        !Number.isFinite(b)
    ) {
        return null;
    }

    const points = [];

    for (let i = 0; i <= 360; i += 1) {
        const age =
            GRAPH_MAX_AGE * i / 360;

        const survival =
            gompertzSurvival(age, a, b);

        if (survival === null) {
            return null;
        }

        const x =
            age / GRAPH_MAX_AGE * 100;

        const y =
            100 - survival * 100;

        points.push(
            `${x.toFixed(5)},${y.toFixed(5)}`
        );
    }

    return points.join(' ');
}

function createBaselinePoints(survival) {
    if (!Array.isArray(survival)) {
        return '';
    }

    const points = [];

    for (
        let age = 0;
        age < survival.length;
        age += 1
    ) {
        const x =
            age / GRAPH_MAX_AGE * 100;

        const y =
            100 - survival[age] * 100;

        points.push(
            `${x.toFixed(5)},${y.toFixed(5)}`
        );
    }

    return points.join(' ');
}

function maskFromSelection(selected) {
    let mask = 0;

    selected.forEach((enabled, index) => {
        if (enabled) {
            mask |= (1 << index);
        }
    });

    return mask;
}

export default function LongevityCalculator() {
    const { t } = useTranslation();

    const [sex, setSex] = useState('m');

    const [age, setAge] = useState(0);

    const [selected, setSelected] = useState(
        () => Array(FACTOR_COUNT).fill(true)
    );

    const mask = useMemo(
        () => maskFromSelection(selected),
        [selected]
    );

    const sexStates =
        longevityData.states?.[sex];

    const baseline =
        longevityData.baseline?.[sex];

    const state =
        sexStates?.[age]?.[mask] ?? null;

    const baselineMedian =
        baseline?.median ??
        sexStates?.[0]?.[0]?.[2] ??
        null;

    const forecastMedian =
        state?.[2] ??
        baselineMedian;

    const difference =
        forecastMedian !== null &&
        baselineMedian !== null
            ? forecastMedian - baselineMedian
            : null;

    const baselinePoints =
        createBaselinePoints(
            baseline?.survival
        );

    const forecastPoints =
        mask === 0
            ? baselinePoints
            : createForecastPoints(state);

    const factorNames =
        t(
            `longevityCalculator.factors.${sex}`,
            {
                returnObjects: true,
            }
        );

    const safeFactorNames =
        Array.isArray(factorNames)
            ? factorNames
            : [];

    const selectedCount =
        selected.filter(Boolean).length;

    const baselinePointLeft =
        baselineMedian !== null
            ? clamp(
                baselineMedian /
                    GRAPH_MAX_AGE *
                    100,
                0,
                100
            )
            : 0;

    const forecastPointLeft =
        forecastMedian !== null
            ? clamp(
                forecastMedian /
                    GRAPH_MAX_AGE *
                    100,
                0,
                100
            )
            : 0;

    function selectAll() {
        setSelected(
            Array(FACTOR_COUNT).fill(true)
        );
    }

    function clearAll() {
        setSelected(
            Array(FACTOR_COUNT).fill(false)
        );
    }

    function changeSex(newSex) {
        setSex(newSex);

        setSelected(
            Array(FACTOR_COUNT).fill(true)
        );
    }

    function changeAge(event) {
        const value =
            Number(event.target.value);

        if (!Number.isFinite(value)) {
            setAge(0);
            return;
        }

        setAge(
            ''+clamp(
                Math.round(value),
                0,
                MAX_START_AGE
            )
        );
    }

    function toggleFactor(index) {
        setSelected(current => {
            const next = [...current];

            next[index] = !next[index];

            return next;
        });
    }

    return (
        <div id="longevity-calculator-v1">

            {/* =====================================================
                METRICS
            ===================================================== */}

            <div className="lc-metrics-slot">

                <div className="lc-metrics">

                    <div className="lc-card lc-card-blue">
                        <div className="lc-card-label">
                            <Trans
                                i18nKey="longevityCalculator.metrics.baseline"
                            />
                        </div>

                        <div className="lc-card-value lc-blue">
                            {formatNumber(baselineMedian)}
                        </div>
                    </div>


                    <div className="lc-card lc-card-green">
                        <div className="lc-card-label">
                            <Trans
                                i18nKey="longevityCalculator.metrics.extended"
                            />
                        </div>

                        <div className="lc-card-value lc-green">
                            {formatNumber(forecastMedian)}
                        </div>
                    </div>


                    <div className="lc-card lc-card-purple">
                        <div className="lc-card-label">
                            <Trans
                                i18nKey="longevityCalculator.metrics.difference"
                            />
                        </div>

                        <div className="lc-card-value lc-purple">
                            {difference === null
                                ? '—'
                                : `${
                                    difference >= 0
                                        ? '+'
                                        : ''
                                }${difference.toFixed(1)}`
                            }
                        </div>
                    </div>

                </div>
            </div>


            {/* =====================================================
                CHART
            ===================================================== */}

            <section className="lc-chart-card">

                <h2 className="lc-chart-title">
                    <Trans
                        i18nKey="longevityCalculator.title"
                    />
                </h2>


                <div className="lc-chart-shell">

                    <div className="lc-plot">

                        <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            className="lc-svg"
                            aria-label={t(
                                'longevityCalculator.chart.ariaLabel'
                            )}
                        >

                            {[0, 25, 50, 75, 100].map(
                                value => (
                                    <line
                                        key={value}
                                        x1="0"
                                        y1={value}
                                        x2="100"
                                        y2={value}
                                        className={
                                            value === 50
                                                ? 'lc-grid lc-grid-50'
                                                : 'lc-grid'
                                        }
                                    />
                                )
                            )}


                            {baselinePoints && (
                                <polygon
                                    points={[
                                        '0,100',
                                        baselinePoints,
                                        `${100 / GRAPH_MAX_AGE * 100},100`,
                                    ].join(' ')}
                                    className="lc-baseline-fill"
                                />
                            )}


                            {baselinePoints && (
                                <polyline
                                    points={baselinePoints}
                                    className="lc-line lc-baseline-line"
                                />
                            )}


                            {forecastPoints && (
                                <polyline
                                    points={forecastPoints}
                                    className="lc-line lc-forecast-line"
                                />
                            )}

                        </svg>


                        {[0, 20, 40, 60, 80, 100, 120].map(
                            value => (
                                <div
                                    key={`x-${value}`}
                                    className="lc-x-tick"
                                    style={{
                                        left:
                                            `${value / GRAPH_MAX_AGE * 100}%`,
                                    }}
                                >
                                    {value}
                                </div>
                            )
                        )}


                        {[0, 25, 50, 75, 100].map(
                            value => (
                                <div
                                    key={`y-${value}`}
                                    className="lc-y-tick"
                                    style={{
                                        top:
                                            `${100 - value}%`,
                                    }}
                                >
                                    {value}
                                </div>
                            )
                        )}


                        <div className="lc-x-label">
                            <Trans
                                i18nKey="longevityCalculator.chart.ageAxis"
                            />
                        </div>


                        <div className="lc-y-label">
                            <Trans
                                i18nKey="longevityCalculator.chart.survivalAxis"
                            />
                        </div>


                        {baselineMedian !== null && (
                            <>
                                <div
                                    className="
                                        lc-median-point
                                        lc-point-blue
                                    "
                                    style={{
                                        left:
                                            `${baselinePointLeft}%`,
                                        top: '50%',
                                    }}
                                />

                                <div
                                    className="
                                        lc-median-label
                                        lc-median-blue
                                    "
                                    style={{
                                        left:
                                            `${baselinePointLeft}%`,
                                        top: '50%',
                                    }}
                                >
                                    {baselineMedian.toFixed(1)}
                                </div>
                            </>
                        )}


                        {forecastMedian !== null && (
                            <>
                                <div
                                    className="
                                        lc-median-point
                                        lc-point-green
                                    "
                                    style={{
                                        left:
                                            `${forecastPointLeft}%`,
                                        top: '50%',
                                    }}
                                />

                                <div
                                    className="
                                        lc-median-label
                                        lc-median-green
                                    "
                                    style={{
                                        left:
                                            `${forecastPointLeft}%`,
                                        top: '50%',
                                    }}
                                >
                                    {forecastMedian.toFixed(1)}
                                </div>
                            </>
                        )}

                    </div>
                </div>


                <div className="lc-legend">

                    <span className="lc-legend-item">
                        <span
                            className="
                                lc-legend-line
                                lc-legend-blue
                            "
                        />

                        <Trans
                            i18nKey="longevityCalculator.chart.baseline"
                        />
                    </span>


                    <span className="lc-legend-item">
                        <span
                            className="
                                lc-legend-line
                                lc-legend-green
                            "
                        />

                        <Trans
                            i18nKey="longevityCalculator.chart.extended"
                        />
                    </span>

                </div>


                <div className="lc-selected-count">
                    {t(
                        'longevityCalculator.model.selected',
                        {
                            count: selectedCount,
                        }
                    )}
                </div>

            </section>


            {/* =====================================================
                CONTROLS
            ===================================================== */}

            <section className="lc-controls">

                <h2>
                    <Trans
                        i18nKey="longevityCalculator.model.title"
                    />
                </h2>


                <p>
                    <Trans
                        i18nKey="longevityCalculator.model.description"
                    />
                </p>


                <div className="lc-control-title">
                    <Trans
                        i18nKey="longevityCalculator.model.sex"
                    />
                </div>


	    <div className="lc-sex-buttons">

	    <button
	    type="button"
	    className={`lc-sex-button ${sex === 'm' ? 'active' : ''}`}
	    onClick={() => changeSex('m')}
	    >
	    <Trans i18nKey="longevityCalculator.model.male" />
	    </button>

	    <button
	    type="button"
	    className={`lc-sex-button ${sex === 'w' ? 'active' : ''}`}
	    onClick={() => changeSex('w')}
	    >
	    <Trans i18nKey="longevityCalculator.model.female" />
	    </button>

	    </div>

                <div className="lc-control-title">
                    <Trans
                        i18nKey="longevityCalculator.model.startAge"
                    />
                </div>


                <div className="lc-age-hint">
                    <Trans
                        i18nKey="longevityCalculator.model.ageHint"
                    />
                </div>


                <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={age}
                    onChange={changeAge}
                    className="lc-age-input"
                />


                <div className="lc-factor-title">
                    <Trans
                        i18nKey="longevityCalculator.model.factorsTitle"
                    />
                </div>


                <div className="lc-buttons">

                    <button
                        type="button"
                        className="lc-action"
                        onClick={selectAll}
                    >
                        <Trans
                            i18nKey="longevityCalculator.model.selectAll"
                        />
                    </button>


                    <button
                        type="button"
                        className="lc-action"
                        onClick={clearAll}
                    >
                        <Trans
                            i18nKey="longevityCalculator.model.clearAll"
                        />
                    </button>

                </div>


                <div className="lc-factor-list">

                    {safeFactorNames.map(
                        (name, index) => (
                            <label
                                key={index}
                                className="lc-factor-row"
                            >

                                <input
                                    type="checkbox"
                                    className="lc-factor-checkbox"
                                    checked={
                                        Boolean(
                                            selected[index]
                                        )
                                    }
                                    onChange={() =>
                                        toggleFactor(index)
                                    }
                                />

                                <span>
                                    {name}
                                </span>

                            </label>
                        )
                    )}

                </div>

            </section>


            {/* =====================================================
                EXPLAINER
            ===================================================== */}

            <section className="lc-explainer">

                <h2>
                    <Trans
                        i18nKey="longevityCalculator.explainer.title"
                    />
                </h2>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.explainer.notPersonal"
                    />
                </p>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.explainer.population"
                    />
                </p>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.explainer.populationSurvival"
                    />
                </p>

            </section>


            {/* =====================================================
                PERSONAL
            ===================================================== */}

            <section className="lc-personal">

                <h2>
                    <Trans
                        i18nKey="longevityCalculator.personal.title"
                    />
                </h2>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.personal.description"
                    />
                </p>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.personal.medical"
                    />
                </p>

                <p>
                    <Trans
                        i18nKey="longevityCalculator.personal.service"
                    />
                    {' '}
                    <Trans
                        i18nKey="longevityCalculator.personal.serviceSteps"
                    />
                </p>

            </section>

        </div>
    );
}
